import { Link } from "react-router-dom";
import { ObjectSchema } from "../redux/actions";
import { currencyFormatter, getMagicNumber } from "./numberHelper";
import {
  camelCaseToWords,
  capitalizeFirstLetter,
  checkEmptyString,
  getFormattedNumber,
  getUppercaseLetterFromOffset,
  itemLinkEqualsCurrentPath,
  lowercaseFirstLetter,
  lrValidator,
  removeSymbols,
  toTitleCase
} from "./stringHelper";

import { detect } from "detect-browser";
const detectBrowser = detect();

import { LanguageSchema } from "../i18n";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";

import { XMLParser } from "fast-xml-parser";

const xmlParser = new XMLParser({
  ignoreAttributes: false,
  attributeNamePrefix: "",
  removeNSPrefix: true // removes ds:, md: etc.
});

dayjs.extend(customParseFormat);
dayjs.extend(utc);

const parser = new DOMParser();

let docError;
let parsererrorNS;

if (detectBrowser && detectBrowser.name !== "ie") {
  docError = parser.parseFromString("INVALID", "text/xml");
  parsererrorNS = docError.getElementsByTagName("parsererror")[0].namespaceURI;
}

export const getFormErrorMessages = (i18n: LanguageSchema) => {
  return {
    required: "${label} " + i18n.IS_REQUIRED,
    string: {
      min: "${label} should not be less than ${min} characters",
      max: "${label} should not be greater than ${max} characters"
    }
  };
};

export const handleLocalStorageClear = () => {
  const preserveKeys = ["isLRMigrated", "LoginRadiusIgnoreMobileView", "showHostedPageBanner"];
  Object.keys(localStorage).forEach((key) => {
    if (!preserveKeys.includes(key)) {
      localStorage.removeItem(key);
    }
  });
};

// util method to prepare data mappings
export const getDataMapping = (data: string[]) => {
  const sortedData = [...data].sort();
  const dataFields = {};
  for (let attr = 0; attr < sortedData.length; attr++) {
    let str = sortedData[attr];
    str = str
      .replace(/-/g, " ")
      .replace(/cf_/g, "")
      .replace(/(^|\s)([a-z])/g, function (m, p1, p2) {
        return p1 + p2.toUpperCase();
      })
      .replace(/([a-z])([A-Z])/g, "$1 $2");
    dataFields[sortedData[attr]] = str.trim();
  }
  return dataFields;
};

export const appendHttps = (url: string) => {
  if (!(url.startsWith("https://") || url.startsWith("http://"))) {
    return "https://" + url;
  } else {
    return url;
  }
};

export const validateEmail = (email: string) => {
  /* eslint-disable no-useless-escape */
  const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
};

export const validateUrl = (url: string) => {
  const urlPattern = new RegExp(/(^|\s)((https?:\/\/)?[\w-]+(\.[\w-]+)+\.?(:\d+)?(\/\S*)?)/i);
  return urlPattern.test(url);
};
export const validateLatter = (test) => {
  const pattern = new RegExp(/^(^[0-9A-Za-z]+|(^[^_!@#$%^&*(),.?":{}|<>]+[0-9A-Za-z]+)|(^[^_!@#$%^&*(),.?":{}|<>]+[0-9A-Za-z_+'=/-]+))+[a-zA-Z0-9]+$/, "gi");
  return pattern.test(test);
};

export const generatePassword = () => {
  const length = 8;
  const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let retVal = "";
  for (let i = 0, n = charset.length; i < length; ++i) {
    retVal += charset.charAt(Math.floor(Math.random() * n));
  }
  return retVal + "1@";
};

export const generateTokenSeed = () => {
  const length = 6;
  const charset = "123456789";
  let retVal = "";
  for (let i = 0, n = charset.length; i < length; ++i) {
    retVal += charset.charAt(Math.floor(Math.random() * n));
  }
  return retVal;
};

export const getUrlWithParams = (url: string, params: any, hash?: string) => {
  const urlParams = new URLSearchParams(params);
  return url + "?" + urlParams.toString() + (hash || "");
};

export const getRootDomain = () => {
  try {
    const hostname = window.location.hostname; // e.g., 'console.lrinternal.com'
    const parts = hostname.split(".");
    if (parts.length >= 2) {
      return `.${parts.slice(-2).join(".")}`; // e.g., '.lrinternal.com' or '.loginradius.com'
    }
    return hostname;
  } catch (error) {
    return "";
  }
};

export const setCurrentAppInfo = (name: string) => {
  localStorage.setItem("siteName", name);
};

export const readTextFile = (file) => {
  return new Promise((resolve: any, reject: any) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result);
    };

    reader.onerror = reject;

    reader.readAsText(file);
  });
};

export const parseXML = (xmlString: string) => {
  let doc: any;
  try {
    doc = parser.parseFromString(xmlString, "text/xml");
  } catch (err) {
    return false;
  }
  if (detectBrowser && detectBrowser.name !== "ie" && doc.getElementsByTagNameNS(parsererrorNS || "", "parsererror").length > 0) {
    return false;
  }
  const root = document.createElement("div");
  root.appendChild(doc.documentElement);

  return root.innerHTML;
};

export const getTagName = (xmlString: string) => {
  const doc = parser.parseFromString(xmlString, "text/xml");
  return doc.documentElement.getAttribute("name") ? doc.documentElement.nodeName + "-" + doc.documentElement.getAttribute("name") : doc.documentElement.nodeName;
};

// HTTPS required; plain http allowed only for loopback hosts (RFC 8252); no fragment.
export const isValidRedirectUri = (raw: string): boolean => {
  try {
    const url = new URL(raw);
    if (url.hash) {
      return false;
    }
    // Unlike Go's url.Hostname(), the WHATWG URL API keeps the brackets around an IPv6
    // host ("http://[::1]/cb" -> "[::1]"), so strip them before comparing or the ::1
    // loopback case never matches what Core (utility.isLoopbackHost) actually accepts.
    const host = url.hostname.toLowerCase().replace(/^\[|\]$/g, "");
    const isLoopback = host === "localhost" || host === "127.0.0.1" || host === "::1";
    if (url.protocol === "https:") {
      return true;
    }
    return url.protocol === "http:" && isLoopback;
  } catch {
    return false;
  }
};

export const isValidUrl = (url: string) => {
  try {
    new URL("/", url);
    return true;
  } catch (_) {
    return false;
  }
};

// Strict URL validator that mirrors the backend's utility.IsValidUrl intent:
// only http(s)/ftp schemes, multi-label public hostname with a 2+ letter TLD;
// rejects localhost, IPv4/IPv6 literals, single-label hosts, and non-web schemes.
// Use ONLY for fields whose backend validator is utility.IsValidUrl - do not
// adopt globally; existing isValidUrl callers stay on permissive parsing.
export const isStrictValidUrl = (url: string): boolean => {
  if (typeof url !== "string") return false;
  const trimmed = url.trim();
  if (!trimmed) return false;

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch (_) {
    return false;
  }

  const protocol = parsed.protocol.toLowerCase();
  if (protocol !== "http:" && protocol !== "https:" && protocol !== "ftp:") {
    return false;
  }

  const hostname = parsed.hostname.toLowerCase();
  if (!hostname) return false;
  if (hostname === "localhost") return false;

  // Reject IPv6 literals (URL parser preserves the bracketed form, and bare
  // IPv6 always contains a colon).
  if (hostname.startsWith("[") || hostname.includes(":")) return false;

  // Reject IPv4 literals.
  if (/^(\d{1,3}\.){3}\d{1,3}$/.test(hostname)) return false;

  // Require a multi-label hostname with a 2+ letter alpha TLD. Each label must
  // start/end with alphanumeric and may contain hyphens in the middle.
  const hostnameRegex = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/;
  return hostnameRegex.test(hostname);
};

export const isValidColor = (strColor: string) => {
  const s = new Option().style;
  s.color = strColor;
  return s.color !== "";
};

// Internal to this module: the only caller is validateIpEntryFormat below. Not exported —
// callers should use validateIpEntryFormat rather than assembling their own IP checks.
const containsDuplicates = (array) => {
  const elementMap = {};
  let atLeastOneDuplicate = false;
  const arrayLength = array.length;
  for (let i = 0; i < arrayLength; i++) {
    const currentDomain = array[i];
    if (elementMap[currentDomain]) {
      atLeastOneDuplicate = true;
      break;
    } else elementMap[currentDomain] = true;
  }
  return atLeastOneDuplicate;
};

const isNotLeadingZeroIp = (ip: string) => {
  const octets = ip.split(".");
  for (let i = 0; i < octets.length; i++) {
    if (octets[i].length > 1 && octets[i].startsWith("0")) {
      return false;
    }
  }
  return true;
};
const isValidIp = (ip: string) => {
  /* eslint-disable no-useless-escape */
  const ipRegexCheck = /^((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){3}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})$/;
  return ipRegexCheck.test(ip);
};

const CompareTwoIPs = (ip1: any, ip2: any) => {
  const ip1Split = ip1.split(".");
  const ip2Split = ip2.split(".");

  const ip1ToInt = ((+ip1Split[0] * 256 + +ip1Split[1]) * 256 + +ip1Split[2]) * 256 + +ip1Split[3];
  const ip2ToInt = ((+ip2Split[0] * 256 + +ip2Split[1]) * 256 + +ip2Split[2]) * 256 + +ip2Split[3];
  if (ip1ToInt < ip2ToInt) {
    return true;
  } else {
    return false;
  }
};

// --- IP entry validation (IPv4 + IPv6, single / range / CIDR) -----------------
// Format sanity only. Overlap and cross-list conflicts are validated server-side
// (ErrorCode 8018) because that check is range- and family-aware and changes with
// the API; duplicating it here would only drift. See the IP Access Management page.

// Characters the API accepts across every entry form: hex digits, dot, colon,
// slash, hyphen. Anything else is rejected upfront (mirrors ErrorCode 8019).
const IP_ALLOWED_CHARS = /^[0-9a-fA-F.:/-]+$/;

// Compressed-form IPv6 (`::` run allowed once). Zone indices (`%eth0`) and
// IPv4-mapped forms are intentionally not accepted — the API does not take them.
const IPV6_PATTERN =
  /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:(:[0-9a-fA-F]{1,4}){1,6}|:((:[0-9a-fA-F]{1,4}){1,7}|:))$/;

export const isValidIpV6 = (ip: string) => IPV6_PATTERN.test(ip.trim());

/**
 * IP family of a well-formed address.
 *
 * @param {string} ip address to classify.
 * @return {"v4" | "v6" | null} the family, or null when the value is neither.
 */
export const ipFamily = (ip: string): "v4" | "v6" | null => {
  const value = (ip || "").trim();
  if (isValidIp(value)) return "v4";
  if (isValidIpV6(value)) return "v6";
  return null;
};

export interface IpEntryValidationMessages {
  required: string;
  charset: string;
  address: string;
  cidr: string;
  range: string;
  rangeOrder: string;
  familyMismatch: string;
  leadingZero: string;
  list: string;
  duplicates: string;
}

// One address, shared by every entry form below. `invalidMessage` differs per form so the
// error names the shape the user was actually typing (a bad bound in a range reads as a
// range error, not a bare address error).
const validateIpAddress = (address: string, messages: IpEntryValidationMessages, invalidMessage: string): true | string => {
  const family = ipFamily(address);
  if (!family) return invalidMessage;
  // Leading-zero octets are an IPv4-only concern; the API rejects them.
  if (family === "v4" && !isNotLeadingZeroIp(address)) return messages.leadingZero;
  return true;
};

// Comma-separated list. Plain addresses only — a range or CIDR must be entered on its own,
// and each part carries its own charset check because the list is split before validation.
const validateIpList = (value: string, messages: IpEntryValidationMessages): true | string => {
  const parts = value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length < 2) return messages.list;
  for (const part of parts) {
    if (part.includes("-") || part.includes("/")) return messages.list;
    if (!IP_ALLOWED_CHARS.test(part)) return messages.charset;
    const result = validateIpAddress(part, messages, messages.list);
    if (result !== true) return result;
  }
  if (containsDuplicates(parts)) return messages.duplicates;
  return true;
};

// CIDR block: one address, one prefix. The prefix must have no leading zeros and stay
// within the address family's bit width.
const validateIpCidr = (value: string, messages: IpEntryValidationMessages): true | string => {
  const segments = value.split("/");
  if (segments.length !== 2) return messages.cidr;
  const [address, prefix] = segments;
  const result = validateIpAddress(address, messages, messages.cidr);
  if (result !== true) return result;
  if (!/^(0|[1-9]\d{0,2})$/.test(prefix)) return messages.cidr;
  const maxPrefix = ipFamily(address) === "v6" ? 128 : 32;
  if (Number(prefix) > maxPrefix) return messages.cidr;
  return true;
};

// Hyphenated range. IPv6 addresses use colons, never hyphens, so splitting on "-" is
// unambiguous for both families.
const validateIpRange = (value: string, messages: IpEntryValidationMessages): true | string => {
  const bounds = value.split("-");
  if (bounds.length !== 2) return messages.range;
  for (const bound of bounds) {
    const result = validateIpAddress(bound, messages, messages.range);
    if (result !== true) return result;
  }
  if (ipFamily(bounds[0]) !== ipFamily(bounds[1])) return messages.familyMismatch;
  // Ordering is only checked for IPv4 here; the API validates IPv6 bounds (8017/8018).
  if (ipFamily(bounds[0]) === "v4" && !CompareTwoIPs(bounds[0], bounds[1])) return messages.rangeOrder;
  return true;
};

/**
 * Validate one entry as typed into the IP rules input: a single address, a
 * hyphenated range, a CIDR block, or a comma-separated list of single addresses.
 * IPv4 and IPv6 are both accepted.
 *
 * Dispatch only — each form is validated by its own helper above. Order matters: the list
 * form is checked before the whole-value charset test because it validates each part
 * separately, and "/" is checked before "-" so a CIDR is never read as a range.
 *
 * @param {string} raw entry as typed by the user.
 * @param {IpEntryValidationMessages} messages localized message per failure mode.
 * @return {true | string} true when well-formed, otherwise the message to display.
 */
export const validateIpEntryFormat = (raw: string, messages: IpEntryValidationMessages): true | string => {
  const value = (raw || "").trim();
  if (!value) return messages.required;
  if (value.includes(",")) return validateIpList(value, messages);
  if (!IP_ALLOWED_CHARS.test(value)) return messages.charset;
  if (value.includes("/")) return validateIpCidr(value, messages);
  if (value.includes("-")) return validateIpRange(value, messages);
  return validateIpAddress(value, messages, messages.address);
};

export const isEqualArray = (a: any[] | null | undefined, b: any[] | null | undefined) => {
  // Length check
  if (a === null && b === null) return true;
  else if (!a || !b || a.length !== b.length) return false;

  // Value check
  const aObj = {};
  for (const i of a) aObj[i] = true;

  for (const i of b) {
    if (!aObj[i]) return false;
  }
  return true;
};

/**
 * This function will check the equilty in two array of objects
 * @param a Array of Objects
 * @param b Array of Objects
 */
export const isEqualArrayOfObjects = (a: any[] | null | undefined, b: any[] | null | undefined) => {
  // Length check
  if (!a || !b || a.length !== b.length) return false;

  for (let i = 0; i < a.length; i++) {
    // Object check
    if (typeof a[i] === "object" && typeof b[i] === "object") {
      if (!isEqualObject(a[i], b[i])) {
        return false;
      }
    }
    // Value check
    else if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
};

/**
 * This function compares 2 objects recursively and returns respective boolean value.
 */
export const isEqualObject = (a, b) => {
  if (!a || !b) {
    return a === b;
  }

  if (a instanceof Array || b instanceof Array) {
    return false;
  }
  // Create arrays of property names
  const aProps = Object.keys(a);
  const bProps = Object.keys(b);

  // If number of properties is different,
  // objects are not equivalent
  if (aProps.length !== bProps.length) {
    return false;
  }

  // Assuming that property names in a and b are equal
  for (const prop in a) {
    // type match
    if (typeof a[prop] !== typeof b[prop]) {
      return false;
    }
    // Array comparison
    else if (Array.isArray(a[prop])) {
      if (!Array.isArray(b[prop])) return false;
      const isEqual = isEqualArrayOfObjects(a[prop], b[prop]);
      if (!isEqual) return false;
    }
    // Object comparison
    else if (typeof a[prop] === "object") {
      const isEqual = isEqualObject(a[prop], b[prop]);
      if (!isEqual) return false;
    }
    // Primitive type comparison
    else if (a[prop] !== b[prop]) {
      return false;
    }
  }

  return true;
};

/**
 * This function fires actions based on the type of menu item it is.
 */
export const handleMenuItemType = (parentLink, parentIndex, subSection, subSectionIndex, navigationHandler) => {
  if (subSection.link && subSection.link.startsWith("http")) {
    return (
      <a
        id={"lnk_" + removeSymbols(subSection.displayName)}
        key={subSectionIndex}
        target="_blank"
        rel="noopener noreferrer"
        href={subSection.link}
        className={`dropdown-item ${subSection.isActive ? " active" : ""}`}
      >
        {subSection.displayName}
      </a>
    );
  } else if (subSection.link && subSection.link.includes("/chat")) {
    return (
      <button
        type="button"
        id={"lnk_" + removeSymbols(subSection.displayName)}
        title="Live Chat"
        key={subSectionIndex}
        className={`dropdown-item ${subSection.isActive ? " active" : ""}`}
        style={{ background: "transparent", border: "none", font: "inherit", textAlign: "left" }}
        onClick={() => window.sh_zendesk && window.sh_zendesk("show")}
      >
        {subSection.displayName}
      </button>
    );
  }
  return (
    <Link
      id={"lnk_" + removeSymbols(subSection.displayName)}
      key={subSectionIndex}
      className={`dropdown-item ${subSection.isActive ? " active" : ""}`}
      to={parentLink + (subSection.link || "")}
      onClick={() => navigationHandler(parentIndex, 0, subSectionIndex)}
    >
      {subSection.displayName}
    </Link>
  );
};

/**
 * This function converts timezone.
 * @param datetime A date string to convert timezone
 */
export const convertTimezone = (datetime: string, from: string | null, to?: string) => {
  const datetimeString = datetime.substr(0, 19).replace(" ", "T");
  from = from || datetime.substr(19);
  to = to || "+00:00";
  const rawDatetime = datetimeString + from;
  const splitedTo = to.split(":");
  return dayjs(rawDatetime).utc().add(+splitedTo[0], "hour").add(+splitedTo[1], "minute").format("YYYY-MM-DDTHH:mm:ss") + (to === "+00:00" ? "Z" : to);
};

/**
 * Get the first three letters of the month whose number is provided, where 1 corresponds to Jan (January)
 * and 12 to Dec (December.)
 * @param month Number of month whose three letter string is to be returned.
 */
export const getThreeLetterMonthWordFromNumber = (month: number) => {
  if (month < 1 || month > 12) {
    return `Argument must be a value between 1 and 12, but was ${month}`;
  }
  let word;
  switch (month) {
    case 1:
      word = "Jan";
      break;
    case 2:
      word = "Feb";
      break;
    case 3:
      word = "Mar";
      break;
    case 4:
      word = "Apr";
      break;
    case 5:
      word = "May";
      break;
    case 6:
      word = "Jun";
      break;
    case 7:
      word = "Jul";
      break;
    case 8:
      word = "Aug";
      break;
    case 9:
      word = "Sep";
      break;
    case 10:
      word = "Oct";
      break;
    case 11:
      word = "Nov";
      break;
    case 12:
      word = "Dec";
      break;
  }
  return word;
};

// Key constants for setting support ticket data in LocalStorage

const ForwardSupportTicketConsts = {
  SUPPORT_TICKET_TICKET_CATEGORY: "supportTicket_ticketCategory",
  SUPPORT_TICKET_PRIORITY: "supportTicket_priority",
  SUPPORT_TICKET_TITLE: "supportTicket_title",
  SUPPORT_TICKET_DESCRIPTION: "supportTicket_description"
};

export const setSupportData = (supportPageData: any) => {
  if ("ticketCategory" in supportPageData) {
    localStorage.setItem(ForwardSupportTicketConsts.SUPPORT_TICKET_TICKET_CATEGORY, supportPageData.ticketCategory);
  }
  if ("priority" in supportPageData) {
    localStorage.setItem(ForwardSupportTicketConsts.SUPPORT_TICKET_PRIORITY, supportPageData.priority);
  }
  if ("title" in supportPageData) {
    localStorage.setItem(ForwardSupportTicketConsts.SUPPORT_TICKET_TITLE, supportPageData.title);
  }
  if ("description" in supportPageData) {
    localStorage.setItem(ForwardSupportTicketConsts.SUPPORT_TICKET_DESCRIPTION, supportPageData.description);
  }
};

export const getSupportData = () => {
  const supportData: any = {};

  const ticketCategory = localStorage.getItem(ForwardSupportTicketConsts.SUPPORT_TICKET_TICKET_CATEGORY);
  if (ticketCategory) {
    supportData.ticketCategory = ticketCategory;
  }
  const priority = localStorage.getItem(ForwardSupportTicketConsts.SUPPORT_TICKET_PRIORITY);
  if (priority) {
    supportData.priority = priority;
  }
  const title = localStorage.getItem(ForwardSupportTicketConsts.SUPPORT_TICKET_TITLE);
  if (title) {
    supportData.title = title;
  }
  const description = localStorage.getItem(ForwardSupportTicketConsts.SUPPORT_TICKET_DESCRIPTION);
  if (description) {
    supportData.description = description;
  }

  return supportData;
};

export const clearSupportData = () => {
  localStorage.setItem(ForwardSupportTicketConsts.SUPPORT_TICKET_TICKET_CATEGORY, "");
  localStorage.setItem(ForwardSupportTicketConsts.SUPPORT_TICKET_PRIORITY, "");
  localStorage.setItem(ForwardSupportTicketConsts.SUPPORT_TICKET_TITLE, "");
  localStorage.setItem(ForwardSupportTicketConsts.SUPPORT_TICKET_DESCRIPTION, "");
};

export const getVerificationUrlList = () => {
  const lrVerificationUrls = localStorage.getItem("lrVerificationUrls");

  return lrVerificationUrls !== null ? lrVerificationUrls.split(";") : [""];
};

export const setVerificationUrls = (url: string) => {
  if (url && url.length) {
    url = url.replace(/\s+/g, "").toLowerCase();

    if (!getVerificationUrlList().includes(url)) {
      localStorage.setItem("lrVerificationUrls", getVerificationUrlList().join(";"));
    }
  }
};

export const trimValues = (obj: any) => {
  // Helper function to trim a string
  const trimString = (str) => (typeof str === "string" ? str.trim() : str);

  // Recursive function to traverse the object
  const traverseAndTrim = (item) => {
    if (Array.isArray(item)) {
      // If item is an array, trim each element
      return item.map(traverseAndTrim);
    } else if (typeof item === "boolean") {
      return item.toString();
    } else if (item && typeof item === "object") {
      // If item is an object, trim each key-value pair
      return Object.keys(item).reduce((acc, key) => {
        acc[key] = traverseAndTrim(item[key]);
        return acc;
      }, {});
    }
    // If item is a string or other type, trim it if it's a string
    return trimString(item);
  };

  // Start the trimming process
  return traverseAndTrim(obj);
};

export const trimValuesAndRemoveKeys = (obj: any) => {
  // Helper function to trim a string
  const trimString = (str) => (typeof str === "string" ? str.trim() : str);

  // Recursive function to traverse the object
  const traverseAndTrim = (item) => {
    if (Array.isArray(item)) {
      // If item is an array, trim and filter elements
      return item.map(traverseAndTrim).filter((el) => el && Object.keys(el).length > 0); // Remove empty objects
    } else if (typeof item === "boolean") {
      return item.toString();
    } else if (item && typeof item === "object") {
      // If item is an object, trim each key-value pair
      const trimmedObject = Object.keys(item).reduce((acc, key) => {
        const trimmedValue = traverseAndTrim(item[key]);

        // Skip undefined, null, or empty string values
        if (trimmedValue !== undefined && trimmedValue !== "" && trimmedValue !== null) {
          acc[key] = trimmedValue;
        }
        return acc;
      }, {});

      // Return the object if it has any keys left, otherwise return null to signal removal
      return Object.keys(trimmedObject).length > 0 ? trimmedObject : null;
    }

    // If item is a string or other type, trim it if it's a string
    return trimString(item);
  };

  // Start the trimming process
  const result = traverseAndTrim(obj);

  // Ensure that if the result is null (meaning the root object is empty), we return an empty object/array.
  return result !== null ? result : {};
};

export const isNumericKeyedObject = (value: unknown): value is Record<string, unknown> => {
  if (!value || Array.isArray(value) || typeof value !== "object") {
    return false;
  }

  const keys = Object.keys(value);
  return keys.length > 0 && keys.every((key) => /^\d+$/.test(key));
};

export const normalizeFormListValue = (value: unknown) => {
  if (!isNumericKeyedObject(value)) {
    return value;
  }

  return Object.keys(value)
    .sort((a, b) => Number(a) - Number(b))
    .map((key) => value[key]);
};

/**
 * This function clones the object passed in the argument to it's deepest level
 * @param obj Object
 */
const deepClone = (obj: any) => {
  const newObj: any = {};

  for (const i in obj) {
    // Clone array
    if (obj[i] instanceof Array) {
      const newArr: any = [];
      for (const j in obj[i]) {
        if (typeof obj[i][j] === "object") {
          newArr.push(deepClone(obj[i][j]));
        } else newArr.push(obj[i][j]);
      }
      newObj[i] = newArr;
    }
    // Null handle
    else if (obj[i] === null) {
      newObj[i] = null;
    }
    // Clone object
    else if (typeof obj[i] === "object") {
      newObj[i] = deepClone(obj[i]);
    }
    // Simple value
    else newObj[i] = obj[i];
  }
  return newObj;
};

const keyToLowerCase = (obj: any) => {
  const newObj: any = {};
  for (const i in obj) {
    // Clone array
    if (obj[i] instanceof Array) {
      const newArr: any = [];
      for (const j in obj[i]) {
        if (typeof obj[i][j] === "object") {
          newArr.push(keyToLowerCase(obj[i][j]));
        } else newArr.push(obj[i][j]);
      }
      newObj[i.toLowerCase()] = newArr;
    }
    // Null handle
    else if (obj[i] === null) {
      newObj[i.toLowerCase()] = null;
    }
    // Clone object
    else if (typeof obj[i] === "object") {
      newObj[i.toLowerCase()] = keyToLowerCase(obj[i]);
    }
    // Simple value
    else newObj[i.toLowerCase()] = obj[i];
  }
  return newObj;
};

/**
 * This function flattens the object to its simplest form.
 * @param obj Object needs to be flattned
 */

const flatten = (obj: ObjectSchema<any>) => {
  const temp: ObjectSchema<any> = {};

  for (const key in obj) {
    if (obj[key] instanceof Array) {
      temp[key] = [];
      for (const item of obj[key]) {
        if (typeof item === "object") {
          temp[key].push(flatten(item));
        } else {
          temp[key].push(item);
        }
      }
    } else if (typeof obj[key] === "object") {
      const flatObj = flatten(obj[key]);
      for (const key2 in flatObj) {
        temp[key + "." + key2] = flatObj[key2];
      }
    } else {
      temp[key] = obj[key];
    }
  }

  return temp;
};

/**
 * This function provides an easy way to cancel the pending promise.
 * The pending subscription can be cancelled by calling the cancel() method.
 * @param promise A Promise Object
 */
const makeCancelable = (promise: Promise<any>) => {
  let hasCanceled_ = false;

  const wrappedPromise = new Promise((resolve, reject) => {
    promise.then((val) => (hasCanceled_ ? reject({ isCanceled: true }) : resolve(val))).catch((error) => (hasCanceled_ ? reject({ isCanceled: true }) : reject(error)));
  });

  return {
    promise: wrappedPromise,
    cancel() {
      hasCanceled_ = true;
    }
  };
};
const checkIfArrayHasUniqueItems = (myArray) => {
  return myArray.length === new Set(myArray).size;
};
const arrayHasUniqueObject = (arr, prop, isBoolean) => {
  let isUnique = true;
  const tempArray = arr.reduce((a, d) => {
    if (!a.includes(d[prop])) {
      a.push(d[prop]);
    } else {
      isUnique = false;
    }
    return a;
  }, []);

  return isBoolean ? isUnique : tempArray;
};

/**
 * Returns the Boolean version of the provided string; if the string is not "true", then
 * Boolean value false is returned.
 * @param str   String representing a potential boolean value; ideally either "true" or "false".
 */
const booleanStringToBoolean = (str: string): boolean => {
  return str === "true";
};

const xmlToJson = (xml) => {
  return xmlParser.parse(xml);
};

const loadContent = (src: string, type?: "script" | "link") => {
  return new Promise((resolve, reject) => {
    type = type || "script";

    const ele = document.createElement(type);
    ele.onload = resolve;
    ele.onerror = reject;

    if (ele instanceof HTMLScriptElement) {
      ele.type = "text/javascript";
      ele.src = src;
      document.body.appendChild(ele);
    } else if (ele instanceof HTMLLinkElement) {
      ele.rel = "stylesheet";
      ele.href = src;
      document.head.appendChild(ele);
    }
  });
};

/* eslint-disable @typescript-eslint/naming-convention */
const validationRuleMessages = (i18n: LanguageSchema, display = "") => {
  return {
    required: `${display} ${i18n.REQUIRED}`,
    invalid_json: `${display} ${i18n.INVALID_JSON}`,
    is_same: `${display} ${i18n.IS_SAME}`,
    exists: `${display} ${i18n.EXISTS}`,
    custom_validation: `${display} ${i18n.CUSTOM_VALIDATION}`,
    is_duplicate: `${display} ${i18n.IS_DUPLICATE}`,
    is_exsist: `${display} ${i18n.IS_EXIST}`,
    is_duplicate_url: `${display} ${i18n.IS_DUPLICATE_URL}`,
    matches: `${display} ${i18n.MATCHES}`,
    matches_field: `${display} ${i18n.MATCHES_FIELD}`,
    min_length: `${display} ${i18n.MIN_LENGTH}`,
    max_length: `${display} ${i18n.MAX_LENGTH}`,
    exact_length: `${display} ${i18n.EXACT_LENGTH}`,
    greater_than: `${display} ${i18n.GREATER_THAN}`,
    less_than: `${display} ${i18n.LESS_THAN}`,
    less_than_or_equal_to: `${display} ${i18n.LESS_THAN_OR_EQUAL_TO}`,
    between: `${display} ${i18n.BETWEEN_VALUE}`,
    numeric_no_space_between: `${display} ${i18n.NUMERIC_NO_SPACES_BETWEEN}`,
    alpha: `${display} ${i18n.ALPHA}`,
    alpha_numeric: `${display} ${i18n.ALPHA_NUMERIC}`,
    alphanumeric_combo: `${display} ${i18n.ALPHANUMERIC_COMBO}`,
    alpha_dash: `${display} ${i18n.ALPHA_DASH}`,
    alpha_numeric_dash_combo: `${display} ${i18n.ALPHA_NUMERIC_DASH_COMBO}`,
    alpha_numeric_underscore_dash: `${display} ${i18n.ALPHA_NUMERIC_UNDERSCORE_DASH}`,
    alpha_numeric_space: `${display} ${i18n.ALPHA_NUMERIC_SPACE}`,
    numeric: `${display} ${i18n.NUMERIC}`,
    integer: `${display} ${i18n.INTEGER}`,
    decimal: `${display} ${i18n.DECIMAL}`,
    is_natural: `${display} ${i18n.IS_NATURAL}`,
    is_natural_no_zero: `${display} ${i18n.IS_NATURAL_NO_ZERO}`,
    valid_ca_zip: `${display} ${i18n.VALID_CA_ZIP}`,
    valid_credit_card: `${display} ${i18n.VALID_CREDIT_CARD}`,
    valid_phoneno: `${display} ${i18n.VALID_PHONENO}`,
    valid_url: `${display} ${i18n.VALID_URL}`,
    valid_strict_url: `${display} ${i18n.VALID_STRICT_URL}`,
    valid_ip: `${display} ${i18n.VALID_IP}`,
    valid_email: `${display} ${i18n.VALID_EMAIL}`,
    valid_base64: `${display} ${i18n.VALID_BASE64}`,
    valid_generic: `${display} ${i18n.VALID_GENERIC}`,
    callback_valid_date: `${display} ${i18n.VALID_DATE}`,
    valid_domain: `${display} ${i18n.VALID_DOMAIN}`,
    start_with_alphabet: `${display} ${i18n.START_WITH_ALPHABET}`,
    start_with_alphabet_numeric: `${display} ${i18n.START_WITH_ALPHNUMERIC}`,
    no_spaces: `${display} ${i18n.NO_SPACES}`,
    no_uppercase: `${display} ${i18n.NO_UPPERCASE}`,
    zendesk_name_error: `${i18n.ZENDESK_NAME_ERROR}`,
    valid_color: `${display} ${i18n.VALID_COLOR}`,
    is_invalid: `${display} ${i18n.IDX_IS_INVALID}`,
    email_is_own: `${display} ${i18n.EMAIL_IS_OWN}`,
    colon: `${display} ${i18n.COLON}`,
    max_size: `${display} ${i18n.MAX_SIZE}`,
    min_size: `${display} ${i18n.MIN_SIZE}`,
    file_type: `${display} ${i18n.FILE_TYPE}`,
    allowed_file_types: `${display} ${i18n.ALLOWED_FILE_TYPES}`,
    valid_domain_or_localhost: `${display} ${i18n.VALID_DOMAIN_OR_LOCALHOST}`
  };
};

/**
 * This function performs a diff between the set of source and dest array.
 *
 * The arrays must be only of primitive types: string, number, boolean
 *
 * @return [missingItems, addedItems]
 */
function diffArrays<T = any>(srcArr: T[], destArr: T[]): [T[], T[]] {
  const missingFromSrc: T[] = [];
  const addedToSrc: T[] = [];

  try {
    // Map source and dest arrays
    const srcMap = new Map<T, boolean>();
    const destMap = new Map<T, boolean>();
    for (const item of srcArr) srcMap.set(item, true);
    for (const item of destArr) destMap.set(item, true);

    // Diff source and dest maps
    // eslint-disable-line @typescript-eslint/no-unused-vars
    for (const [key, _] of srcMap) {
      if (!destMap.get(key)) missingFromSrc.push(key);
    }
    // eslint-disable-line @typescript-eslint/no-unused-vars
    for (const [key, _] of destMap) {
      if (!srcMap.get(key)) addedToSrc.push(key);
    }
  } catch (e) {
    console.error(e);
  } finally {
    // eslint-disable-next-line no-unsafe-finally
    return [missingFromSrc, addedToSrc]; //NOSONAR
  }
}
const churnzeroEventLog = (pageName?: string) => {
  if (window.ChurnZero && pageName) {
    window.ChurnZero && window.ChurnZero.push(["trackEvent", pageName, pageName]);
  }
};
const SpecialCapsConversion = [
  {
    from: "Sms",
    to: "SMS"
  },
  {
    from: "Etl",
    to: "ETL"
  },
  {
    from: "Otp",
    to: "OTP"
  },
  {
    from: "Oauth",
    to: "OAuth"
  },
  {
    from: "Saml",
    to: "SAML"
  },
  {
    from: "Adfs",
    to: "ADFS"
  },
  {
    from: "Jwt",
    to: "JWT"
  },
  {
    from: "Idps",
    to: "IDPs"
  },
  {
    from: "Rba",
    to: "RBA"
  },
  {
    from: "Api",
    to: "API"
  },
  {
    from: "Sso",
    to: "SSO"
  },
  {
    from: "Js",
    to: "JS"
  }
];

const _getFormattedPath = (newPath: string) => {
  const pathList = newPath.split("/");
  const pathToArray = pathList.map((path) => {
    // eslint-disable-next-line
    let p = toTitleCase(path.replace(/\-/g, " "));
    for (const conversion of SpecialCapsConversion) {
      p = p.replace(conversion.from, conversion.to);
    }
    return p;
  });
  return pathToArray.join("/");
};

const IdpMetadatTemplate = (idp) => {
  return (
    `<?xml version="1.0" encoding="UTF-8"?>
    <md:EntityDescriptor xmlns:md="#xmlns_metadata#" entityID="#entityID#" xmlns:ds="http://www.w3.org/2000/09/xmldsig#">
    <md:SPSSODescriptor protocolSupportEnumeration="#protocol#">
       <md:KeyDescriptor use="signing">
          <ds:KeyInfo>
             <ds:X509Data>
                <ds:X509Certificate>#spcertificate#</ds:X509Certificate>
             </ds:X509Data>
          </ds:KeyInfo>
       </md:KeyDescriptor>
      <md:NameIDFormat>urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified</md:NameIDFormat>
      <md:AssertionConsumerService Binding="#` +
    (idp ? "bindingidp" : "binding") +
    `#" Location="#sso_location#"/>
    </md:SPSSODescriptor>
  </md:EntityDescriptor>`
  );
};

// Builds the SP metadata XML from a configured SAML/ADFS provider and triggers a browser download.
const downloadIdpMetadataFile = (o: any, serviceURL: string) => {
  const data = "urn:oasis:names:tc:" + (o.SamlVersion === "SAML 1.1" ? "SAML:1.1" : "SAML:2.0") + ":metadata";
  const mapObj = {
    "#xmlns_metadata#": data,
    "#entityID#": serviceURL.split("/").slice(0, 3).join("/"),
    "#protocol#": data.replace("metadata", "protocol"),
    "#spcertificate#": o.SpCertificate.Certificate !== null ? o.SpCertificate.Certificate.replace("-----BEGIN CERTIFICATE-----", "").replace("-----END CERTIFICATE-----", "").trim() : "",
    "#binding#": o.IdentityProvider.Binding,
    "#bindingidp#": o.IdentityProvider.Binding.replace("POST", "Redirect"),
    "#sso_location#": serviceURL
  };
  const idp = o.IsIdpInitiated && o.IdentityProvider.Binding.includes("Redirect") ? false : o.IsIdpInitiated ? true : false;
  let tmp = IdpMetadatTemplate(idp);
  tmp = tmp.replace(/#xmlns_metadata#|#entityID#|#protocol#|#spcertificate#|#binding#|#sso_location#|#bindingidp#/gi, function (matched) {
    return mapObj[matched];
  });
  const uri = "data:application/xml;charset=utf-8," + encodeURIComponent(tmp);
  const downloadLink = document.createElement("a");
  downloadLink.href = uri;
  downloadLink.download = "SAML_" + o.Provider + ".xml";
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
};

const checkOauthApp = (appList: any[], appName: string): string => {
  let errMsg = "";
  for (const app of appList) {
    if (app.AppName === appName) {
      errMsg = "An app already exists";
      break;
    }
  }
  return errMsg;
};

const getConvertedDate = (timezone: string, date?: string): string => {
  if (!date) {
    return "-";
  }
  const tempDate = convertTimezone(date, "+00:00", timezone);
  return dayjs(tempDate.slice(0, 19), "YYYY-MM-DDTHH:mm:ss").format("DD MMM, YYYY h:mm:ss A");
};

const getSparseTickValuesForChart = (data: any[], dataField: string, noOfTicks?: number) => {
  const numberOfTicks = noOfTicks || 10;
  if (data.length > numberOfTicks) {
    const spaceBetweenTicks = Math.floor(data.length / numberOfTicks);

    return data.reduce((acc, item, index) => {
      if (index % spaceBetweenTicks === 0) {
        acc.push(item[dataField]);
      }
      return acc;
    }, []);
  }
  return [];
};
const isTrialPlan = (appInfo): boolean => {
  return appInfo.isSuccess && appInfo.AppName && appInfo.ProductPlan && appInfo.ProductPlan.Name === "trial" ? true : false;
};
// Plan-aware landing/default route:
// - unpaid (trial) users land on the Getting Started page
// - paid users land on the Overview page
const getLandingRoute = (appInfo): string => {
  return isTrialPlan(appInfo) ? "/getting-started" : "/overview";
};
const isTrialPlanExpired = (...appInfos) => {
  const appInfo = appInfos.find((info) => typeof info?.isTrialExpired === "boolean");
  return Boolean(appInfo?.isTrialExpired);
};

const downloadByDataUri = (data: string, fileName: string) => {
  // create a link element and click it to download the resource
  const a = document.createElement("a");
  a.download = fileName;
  a.href = data;
  const clickEvt = new MouseEvent("click", {
    view: window,
    bubbles: true,
    cancelable: true
  });
  a.dispatchEvent(clickEvt);
  a.remove();
};

const isEmptyArray = (arr): boolean => {
  return arr === null || (Array.isArray(arr) && (arr.length === 0 || (arr.length === 1 && arr[0] === "")));
};

const generateId = (location: string, section: string, field: string): string => {
  const sanitize = (str: string) =>
    str
      ?.trim()
      .toLowerCase()
      .replace(/\s+/g, "_") // spaces → underscore
      .replace(/[^a-z0-9_]/g, ""); // remove invalid chars

  const lastPart = location.split("/").filter(Boolean).pop() || "default";

  return `${sanitize(lastPart)}_${sanitize(section)}_${sanitize(field)}`;
};

export const getMaxDateValue = (timezone: string, accountProfile: any): string => {
  if (!timezone || !accountProfile.isSuccess) {
    return dayjs().utc().format("YYYY-MM-DD HH:mm:ss");
  }
  const accountDateTime = convertTimezone(dayjs().utc().format(), null, timezone);
  if (!accountDateTime || !dayjs(accountDateTime).isValid()) {
    return dayjs().utc().format("YYYY-MM-DD HH:mm:ss");
  }
  return dayjs(accountDateTime.substr(0, 19)).format("YYYY-MM-DD HH:mm:ss");
};

/**
 * truncate string and add ... at the end
 * @param str string to truncate
 * @param maxLength length of the string
 * @returns string - with ... at the end if length of input string was more than maxLength
 */
export const truncate = (str: string, maxLength: number) => {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength) + "...";
};

export const formatNumberK = (value: number): string => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  return value.toString();
};

export {
  _getFormattedPath,
  arrayHasUniqueObject,
  booleanStringToBoolean,
  camelCaseToWords,
  capitalizeFirstLetter,
  checkEmptyString,
  checkIfArrayHasUniqueItems,
  checkOauthApp,
  churnzeroEventLog,
  currencyFormatter,
  deepClone,
  diffArrays,
  flatten,
  getConvertedDate,
  getFormattedNumber,
  getMagicNumber,
  getUppercaseLetterFromOffset,
  IdpMetadatTemplate,
  downloadIdpMetadataFile,
  itemLinkEqualsCurrentPath,
  keyToLowerCase,
  loadContent,
  lowercaseFirstLetter,
  lrValidator,
  makeCancelable,
  removeSymbols,
  SpecialCapsConversion,
  toTitleCase,
  validationRuleMessages,
  xmlToJson,
  getSparseTickValuesForChart,
  isTrialPlan,
  getLandingRoute,
  isTrialPlanExpired,
  downloadByDataUri,
  isEmptyArray,
  generateId
};
